import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Send, ArrowLeft, BookOpen } from 'lucide-react';
import { suggestionsService } from '../services/suggestionsService';
import { bookService } from '../services/bookService';
import { useToast } from '../hooks/use-toast';

const RequestQuiz = () => {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState(null);
  const [formData, setFormData] = useState({
    book_id: '',
    difficulty_requested: '',
    reason: '',
    user_email: '',
    user_name: ''
  });

  useEffect(() => {
    if (bookId) {
      fetchBook();
      setFormData(prev => ({ ...prev, book_id: bookId }));
    }
  }, [bookId]);

  const fetchBook = async () => {
    try {
      const bookData = await bookService.getBook(bookId);
      setBook(bookData);
    } catch (error) {
      console.error('Error fetching book:', error);
      toast({
        title: "Error",
        description: "Failed to load book information.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await suggestionsService.createQuizRequest(formData);
      
      toast({
        title: "Quiz Requested Successfully!",
        description: "Thank you for your request. We'll review it and create the quiz if there's enough interest.",
      });
      
      // Reset form
      setFormData({
        book_id: bookId || '',
        difficulty_requested: '',
        reason: '',
        user_email: '',
        user_name: ''
      });
      
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit quiz request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Request a Quiz - BookQuiz"
        description="Request a quiz for a book that doesn't have one yet."
        url="/request-quiz"
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(bookId ? `/book/${bookId}` : '/')}
            className="mb-6 min-h-[44px] touch-manipulation hover:bg-blue-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {bookId ? 'Back to Book' : 'Back to Home'}
          </Button>

          <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-0">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mb-4">
                <Target className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Request a Quiz
              </CardTitle>
              <p className="text-gray-600">
                Request a new quiz for {book ? `"${book.title}"` : 'your favorite book'}
              </p>
            </CardHeader>
            
            <CardContent>
              {book && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <BookOpen className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <h4 className="font-semibold text-blue-900">{book.title}</h4>
                      <p className="text-sm text-blue-700">by {book.author}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="difficulty_requested">Requested Difficulty Level</Label>
                  <Select 
                    value={formData.difficulty_requested} 
                    onValueChange={(value) => handleSelectChange('difficulty_requested', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select difficulty (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Easy">Easy</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank if you don't have a preference
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="reason">Why do you want this quiz? *</Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us why you'd like this quiz. Are you studying for an exam? Teaching a class? Just love the book? The more details, the better!"
                    required
                  />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="user_name">Your Name</Label>
                      <Input
                        id="user_name"
                        name="user_name"
                        value={formData.user_name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="user_email">Your Email</Label>
                      <Input
                        id="user_email"
                        name="user_email"
                        type="email"
                        value={formData.user_email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    We'll notify you when the quiz is ready!
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all hover:scale-105"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Submitting...' : 'Submit Quiz Request'}
                </Button>
              </form>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-purple-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Our team will review your request</li>
                  <li>• We'll prioritize popular requests</li>
                  <li>• If approved, we'll create the quiz using AI</li>
                  <li>• You'll be notified when it's available!</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default RequestQuiz;